/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * WorkflowTopBar (#683, W2 Phase 1) — the shell's primary navigation: one tab
 * per workflow group, with a hover/focus flyout listing that group's surfaces so
 * any destination is one click away.
 *
 * Replaces the collapsible navy sidebar as the top-level nav, and absorbs the
 * old TopBar's account chrome (color mode, role badge, profile, Sign Out) so
 * nothing is lost in the migration.
 *
 * **Visibility is not decided here.** Tabs arrive pre-filtered from
 * `buildNavTabs`, the one projection every nav surface shares (#683 D3) — the
 * shell must not be able to reveal a surface the sidebar would have hidden.
 */

import React from 'react';
import { Badge, Box, Button, chakra, Flex, HStack, Image, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useConfirmExit, useGuardedNavigate } from '../ui/UnsavedWorkGuard';
import type { NavTab } from '../../utils/rbac';
import { ROLE_LABELS } from '../../utils/rbac';
import { useAuth } from '../../hooks';
import { authService } from '../../services';
import { ColorModeButton } from '../ui/color-mode';
import { NavIcon } from './nav-icons';

const ROLE_BADGE_COLORS: Record<string, string> = {
  super_admin: 'error.solid',
  admin: 'accent.solid',
  ad_ops: 'success.solid',
  sales: 'navy',
  viewer: 'neutral.gray',
  demo_user: 'info.solid',
};

/** Delay before a flyout closes, so the pointer can cross the gap into it. */
const FLYOUT_CLOSE_MS = 180;

export interface WorkflowTopBarProps {
  tabs: NavTab[];
  /** Key of the tab owning the current route, if any. */
  activeTabKey?: string;
  /** Path of the current surface, for highlighting inside a flyout. */
  activePath?: string;
  isMobile?: boolean;
  onOpenMenu?: () => void;
}

export const WorkflowTopBar: React.FC<WorkflowTopBarProps> = ({
  tabs,
  activeTabKey,
  activePath,
  isMobile,
  onOpenMenu,
}) => {
  const navigate = useNavigate();
  const { user, role, clearAuth } = useAuth();
  const [openKey, setOpenKey] = React.useState<string | null>(null);
  const closeTimer = React.useRef<number | undefined>(undefined);

  const openFlyout = (key: string) => {
    window.clearTimeout(closeTimer.current);
    setOpenKey(key);
  };
  const scheduleClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenKey(null), FLYOUT_CLOSE_MS);
  };
  React.useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  /**
   * **Asked before the session is thrown away, not after** (`#1245`).
   *
   * Signing out loses unsaved work exactly as a nav away does, and it cannot be guarded at the
   * navigate: the auth clear happens first, so by then there is nothing to go back to.
   */
  const confirmExit = useConfirmExit();
  const handleLogout = async () => {
    if (!(await confirmExit())) return;
    try {
      await authService.logout();
    } finally {
      clearAuth();
      navigate('/login');
    }
  };

  /* One guard for every way this bar moves the operator (`#1245`) — the tabs, the flyouts and the
     profile item all come through here rather than each calling `navigate` and each forgetting. */
  const guardedNavigate = useGuardedNavigate();
  const go = (path: string) => {
    setOpenKey(null);
    guardedNavigate(path);
  };

  return (
    <Box
      as="header"
      bg="bg.nav"
      color="white"
      h="52px"
      flexShrink={0}
      display="flex"
      alignItems="stretch"
      position="relative"
      zIndex={50}
      pl={isMobile ? 3 : 4}
    >
      {isMobile && (
        <Button
          aria-label="Open navigation menu"
          variant="ghost"
          size="sm"
          alignSelf="center"
          color="white"
          _hover={{ bg: 'rgba(255,255,255,0.08)' }}
          onClick={onOpenMenu}
        >
          <NavIcon name="menu" size={20} />
        </Button>
      )}

      <Flex align="center" pr={isMobile ? 0 : 5} mr={1} gap={2} flex={isMobile ? 1 : undefined}>
        <Image src="/assets/logo-mark-white.svg" alt="bidBrain" w="26px" h="26px" flexShrink={0} />
        <Text fontWeight="bold" fontSize="sm" letterSpacing="0.02em" display={{ base: 'none', sm: 'block' }}>
          bidBrain
        </Text>
      </Flex>

      {!isMobile && (
        <Flex as="nav" aria-label="Workflow groups" align="stretch" flex={1} gap="2px" minW={0}>
          {tabs.map((tab) => {
            const isActive = tab.key === activeTabKey;
            const isOpen = openKey === tab.key;
            const isLocked = tab.kind === 'locked';

            return (
              <Box
                key={tab.key}
                position="relative"
                display="flex"
                alignItems="stretch"
                onMouseEnter={() => !isLocked && tab.kind === 'group' && openFlyout(tab.key)}
                onMouseLeave={scheduleClose}
              >
                <chakra.button
                  type="button"
                  disabled={isLocked}
                  aria-haspopup={tab.kind === 'group' ? 'true' : undefined}
                  aria-expanded={tab.kind === 'group' ? isOpen : undefined}
                  aria-current={isActive ? 'page' : undefined}
                  title={isLocked ? 'Not available in the demo workspace' : undefined}
                  onFocus={() => tab.kind === 'group' && openFlyout(tab.key)}
                  onBlur={scheduleClose}
                  onClick={() => {
                    if (tab.kind === 'direct') go(tab.item.path);
                    else if (tab.kind === 'group') go(tab.items[0].path);
                  }}
                  position="relative"
                  display="flex"
                  alignItems="center"
                  gap={2}
                  px={4}
                  fontSize="13.5px"
                  fontWeight={isActive ? '600' : '400'}
                  color={isLocked ? 'rgba(255,255,255,0.38)' : isActive || isOpen ? 'white' : 'rgba(255,255,255,0.72)'}
                  bg={isActive ? 'rgba(155,127,232,0.14)' : isOpen ? 'rgba(255,255,255,0.06)' : 'transparent'}
                  cursor={isLocked ? 'not-allowed' : 'pointer'}
                  transition="color 0.15s, background 0.15s"
                  whiteSpace="nowrap"
                >
                  <NavIcon
                    name={isLocked ? 'lock' : tab.icon}
                    size={15}
                    style={{ flexShrink: 0, color: isActive ? 'var(--chakra-colors-accent-subtle)' : 'currentColor' }}
                  />
                  <span>{tab.label}</span>
                  {isActive && (
                    <Box
                      position="absolute"
                      left="8px"
                      right="8px"
                      bottom={0}
                      h="2.5px"
                      borderTopRadius="2px"
                      bg="accent.solid"
                    />
                  )}
                </chakra.button>

                {tab.kind === 'group' && isOpen && (
                  <Box
                    position="absolute"
                    top="100%"
                    left="8px"
                    minW="210px"
                    p={1.5}
                    zIndex={60}
                    bg="bg.card"
                    borderWidth="1px"
                    borderColor="border.default"
                    borderRadius="8px"
                    boxShadow="lg"
                  >
                    {tab.items.map((item) => {
                      const current = item.path === activePath;
                      return (
                        <chakra.button
                          key={item.path}
                          type="button"
                          w="100%"
                          display="flex"
                          alignItems="center"
                          gap={2.5}
                          px={2.5}
                          py={2}
                          borderRadius="6px"
                          fontSize="13.5px"
                          textAlign="left"
                          color={current ? 'accent.solid' : 'fg.default'}
                          fontWeight={current ? '600' : '400'}
                          bg={current ? 'accent.subtle' : 'transparent'}
                          _hover={{ bg: current ? 'accent.subtle' : 'bg.subtle' }}
                          onClick={() => go(item.path)}
                        >
                          <NavIcon
                            name={item.icon}
                            size={15}
                            style={{ flexShrink: 0, color: current ? 'currentColor' : 'var(--chakra-colors-fg-muted)' }}
                          />
                          <Text whiteSpace="nowrap">{item.label}</Text>
                        </chakra.button>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Flex>
      )}

      <HStack gap={{ base: 1, md: 3 }} px={{ base: 2, md: 4 }} align="center" flexShrink={0}>
        <ColorModeButton />
        {role && (
          <Badge
            display={{ base: 'none', lg: 'inline-flex' }}
            bg={ROLE_BADGE_COLORS[role] ?? 'neutral.gray'}
            color="white"
            px={2}
            py={0.5}
            borderRadius="full"
            fontSize="xs"
            fontWeight="600"
          >
            {ROLE_LABELS[role]}
          </Badge>
        )}
        {user && (
          <Button
            display={{ base: 'none', md: 'inline-flex' }}
            size="sm"
            variant="ghost"
            color="rgba(255,255,255,0.85)"
            _hover={{ bg: 'rgba(255,255,255,0.1)', color: 'white' }}
            onClick={() => go('/profile?self=1')}
          >
            {user.name}
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          borderColor="rgba(255,255,255,0.25)"
          color="white"
          _hover={{ bg: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.45)' }}
          onClick={handleLogout}
        >
          Sign Out
        </Button>
      </HStack>
    </Box>
  );
};
